import React, { useState, useMemo, useEffect ,useCallback} from "react";
import {
  BarChart,
  Bar,
  Cell,
  LabelList,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Users,
  Activity,
  CalendarCheck,
  UserPlus,
  CalendarX,
  Eye,
  CalendarClock,
  ChevronDown,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { useTheme } from "../../context/themeContext";
import { useAuth } from "../../context/authContext";
import { BRAND, TOKENS, CardShell } from "./dashboardShared";
import serverUrl from "../../utils/utilsser";
import { DEMO_DATA } from "../../utils/demoData";

// ---------------------------------------------------------------------------
// Therapist Analytics — fully self-contained. Fetches its own data via
// useAuth()'s `code`, so it can be dropped into any page/route on its own:
//
//   import TherapistAnalytics from "./TherapistAnalytics";
//   <TherapistAnalytics />
//
// Replace the dummy endpoint below with the real one when ready.
// ---------------------------------------------------------------------------

// Age buckets shown on the "Patients Overview" bar chart.
// `key` is what we look up in the API payload, `label` is what's shown on the axis.
const AGE_BUCKETS = [
  { key: "<18", label: "<18" },
  { key: "18-25", label: "18-25" },
  { key: "26-35", label: "26-35" },
  { key: "36-45", label: "36-45" },
  { key: "46-60", label: "46-60" },
  { key: "61-80", label: "61-80" },
];

function PulseStyles() {
  return (
    <style>{`
      @keyframes ta-plus-one-pop {
        0%   { opacity: 0; transform: translate(-50%, 4px) scale(0.7); }
        15%  { opacity: 1; transform: translate(-50%, -4px) scale(1.1); }
        30%  { transform: translate(-50%, -8px) scale(1); }
        100% { opacity: 0; transform: translate(-50%, -26px) scale(1); }
      }
      .ta-plus-one-badge {
        position: absolute;
        top: -10px;
        left: 50%;
        font-size: 11px;
        font-weight: 800;
        color: #fff;
        background: linear-gradient(90deg, #ef5da8, #8b7cf6);
        padding: 2px 8px;
        border-radius: 9999px;
        box-shadow: 0 4px 12px rgba(139,124,246,0.5);
        animation: ta-plus-one-pop 1.6s ease-out forwards;
        pointer-events: none;
        z-index: 30;
        white-space: nowrap;
      }
    `}</style>
  );
}

// The API sample the team shared has a couple of typo'd keys (e.g. "Montly"
// instead of "Monthly", "61:80" instead of "61-80"). These two helpers
// normalize around that so the chart still renders correctly once the real
// endpoint is wired up, typos and all.
function getRangeObject(patientsgraph, rangeKey) {
  if (!patientsgraph) return null;
  const variants = {
    monthly: ["Monthly", "Montly", "monthly"],
    weekly: ["Weekly", "Weakly", "weekly"],
    yearly: ["Yearly", "yearly"],
  };
  const candidates = variants[rangeKey] || [rangeKey];
  for (const key of candidates) {
    if (patientsgraph[key]) return patientsgraph[key];
  }
  const fallbackKey = Object.keys(patientsgraph)[0];
  return fallbackKey ? patientsgraph[fallbackKey] : null;
}

function getBucketValue(rangeObj, bucketKey) {
  if (!rangeObj) return 0;
  const candidates = [bucketKey, bucketKey.replace("-", ":")];
  for (const key of candidates) {
    if (rangeObj[key] !== undefined && rangeObj[key] !== null) {
      return Number(rangeObj[key]) || 0;
    }
  }
  return 0;
}

const RANGE_OPTIONS = [
  { key: "weekly", label: "This Week" },
  { key: "monthly", label: "This Month" },
  { key: "yearly", label: "This Year" },
];

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

function TherapistStatCard({
  icon: Icon,
  tint,
  title,
  value,
  loading,
  delta,
  pulse,     // NEW
  pulseKey,  // NEW
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const isUp = delta?.trend !== "down";
  const TrendIcon = isUp ? ArrowUpRight : ArrowDownRight;
  const trendColor = isUp ? "#22c55e" : "#ef4444";

  return (
    <div
      className={
        "relative flex items-center gap-3.5 rounded-2xl border px-4 py-4 transition-all duration-300 hover:-translate-y-0.5 " +
        t.card +
        " " +
        t.cardShadow
      }
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 20px 45px -20px ${tint}66`;
        e.currentTarget.style.borderColor = `${tint}55`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "";
      }}
    >
      {pulse && (
        <span key={pulseKey} className="ta-plus-one-badge">
          +1
        </span>
      )}
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: `${tint}1f`, color: tint }}
      >
        <Icon size={19} strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={"truncate text-xs font-medium " + t.textMuted}>
          {title}
        </div>
        <div className={"mt-0.5 text-xl font-bold leading-none tabular-nums " + t.textPrimary}>
          {loading ? "..." : (value ?? "--")}
        </div>
        {delta ? (
          <div className="mt-1.5 flex items-center gap-1 text-[11px]">
            <span className={t.textFaint}>{delta.label ?? "Last year"}</span>
            <span className="flex items-center gap-0.5 font-semibold" style={{ color: trendColor }}>
              <TrendIcon size={12} strokeWidth={2.5} />
              {delta.value}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NEW: skeleton for the Patients Overview chart — mirrors the real card's
// header (title + range pill) and bar-chart footprint (6 bars, one per age
// bucket) so the layout doesn't jump once real data arrives. Pure markup,
// no data/state logic, so it can't affect functionality.
// ---------------------------------------------------------------------------

function PatientsOverviewSkeleton() {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const pulse = isDark ? "bg-white/[0.06]" : "bg-black/[0.06]";

  // Varying heights so it reads as "bars", not a flat block.
  const heights = ["55%", "80%", "100%", "70%", "40%", "60%"];

  return (
    <CardShell accent="purple" className="flex flex-col">
      <div className="mb-1 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={"h-5 w-40 animate-pulse rounded-md " + pulse} />
          <div
            className={"mt-2 h-3.5 w-56 animate-pulse rounded-md " + pulse}
          />
        </div>
        <div
          className={
            "h-8 w-24 shrink-0 animate-pulse rounded-full " + pulse
          }
        />
      </div>

      <div className="mt-4 flex h-[280px] w-full items-end gap-3 sm:gap-5 px-1">
        {heights.map((h, i) => (
          <div
            key={i}
            className="flex flex-1 flex-col items-center justify-end gap-2"
          >
            <div
              className={"w-full animate-pulse rounded-t-lg " + pulse}
              style={{ height: h }}
            />
            <div className={"h-3 w-8 animate-pulse rounded-md " + pulse} />
          </div>
        ))}
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Patients Overview bar chart
// ---------------------------------------------------------------------------

function PatientsOverviewChart({ therapistData, loading, error }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeSel, setRangeSel] = useState("monthly");

  const rangeObj = useMemo(
    () => getRangeObject(therapistData?.data?.patientsgraph, rangeSel),
    [therapistData, rangeSel],
  );

  const chartData = useMemo(
    () =>
      AGE_BUCKETS.map((b) => ({
        label: b.label,
        value: getBucketValue(rangeObj, b.key),
      })),
    [rangeObj],
  );

  const total = chartData.reduce((s, d) => s + d.value, 0);
  const maxIndex = chartData.reduce(
    (best, cur, idx, arr) => (cur.value > arr[best].value ? idx : best),
    0,
  );
  const activeIndex = hoveredIndex ?? maxIndex;
  const currentRangeLabel =
    RANGE_OPTIONS.find((r) => r.key === rangeSel)?.label ?? "This Month";

  if (loading) {
    return <PatientsOverviewSkeleton />;
  }

  if (error) {
    return (
      <CardShell accent="purple" className="flex flex-col">
        Unable to load data.
      </CardShell>
    );
  }

  return (
    <CardShell accent="purple" className="flex flex-col">
      <div className="mb-1 flex items-start justify-between gap-3">
        <div>
          <h2 className={"text-xl font-bold " + t.textPrimary}>
            Patients Overview
          </h2>
          <p className={"mt-1 text-sm " + t.textMuted}>
            Counselling patients grouped by age range
          </p>
        </div>

        <div className="relative shrink-0">
          <button
            onClick={() => setRangeOpen((o) => !o)}
            className={
              "flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors " +
              t.pillBorder +
              " " +
              t.surfaceSoft +
              " " +
              t.textMuted +
              " " +
              t.surfaceHover
            }
          >
            {currentRangeLabel}
            <ChevronDown
              size={13}
              className={
                "transition-transform duration-200 " +
                (rangeOpen ? "rotate-180" : "")
              }
            />
          </button>
          {rangeOpen && (
            <div
              className={
                "absolute right-0 z-10 mt-2 w-36 overflow-hidden rounded-xl border py-1 shadow-xl " +
                t.card +
                " " +
                t.pillBorder
              }
            >
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => {
                    setRangeSel(opt.key);
                    setRangeOpen(false);
                  }}
                  className={
                    "flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium transition-colors " +
                    t.surfaceHover +
                    " " +
                    (rangeSel === opt.key ? t.textPrimary : t.textMuted)
                  }
                >
                  {opt.label}
                  {rangeSel === opt.key ? (
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: BRAND.purple }}
                    />
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 36, right: 8, left: -20, bottom: 0 }}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <CartesianGrid
              vertical={false}
              stroke={t.gridStroke}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fill: t.axisTick, fontSize: 12, fontWeight: 600 }}
              dy={8}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: t.axisTick, fontSize: 11 }}
              allowDecimals={false}
              width={34}
            />
            <Tooltip
              cursor={{
                fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
              }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                const pct =
                  total > 0 ? ((p.value / total) * 100).toFixed(0) : 0;
                return (
                  <div
                    className="rounded-xl border px-3 py-2 text-xs shadow-xl"
                    style={{
                      background: t.tooltipBg,
                      borderColor: t.tooltipBorder,
                    }}
                  >
                    <div
                      className="font-semibold"
                      style={{ color: BRAND.purple }}
                    >
                      {p.label} yrs
                    </div>
                    <div
                      className={
                        isDark
                          ? "mt-0.5 text-slate-300"
                          : "mt-0.5 text-slate-600"
                      }
                    >
                      {p.value} patients · {pct}%
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="value" radius={[10, 10, 4, 4]} maxBarSize={54}>
              {chartData.map((entry, index) => {
                const isActive = index === activeIndex;
                return (
                  <Cell
                    key={entry.label}
                    fill={
                      isActive
                        ? BRAND.purple
                        : isDark
                          ? "rgba(139,124,246,0.16)"
                          : "rgba(139,124,246,0.14)"
                    }
                    style={{ cursor: "pointer", transition: "fill 0.25s ease" }}
                    onMouseEnter={() => setHoveredIndex(index)}
                  />
                );
              })}
              <LabelList
                dataKey="value"
                content={(props) => {
                  const { x, y, width, value, index } = props;
                  const isActive = index === activeIndex;
                  const label = String(value);
                  const w = Math.max(30, label.length * 9 + 18);
                  const h = 22;
                  const cx = x + width / 2;
                  return (
                    <g
                      key={`badge-${index}`}
                      transform={`translate(${cx - w / 2}, ${y - h - 8})`}
                      style={{ pointerEvents: "none" }}
                    >
                      <rect
                        width={w}
                        height={h}
                        rx={h / 2}
                        fill={isActive ? BRAND.purple : t.tooltipBg}
                        stroke={isActive ? BRAND.purple : t.tooltipBorder}
                        strokeWidth="1.5"
                      />
                      <text
                        x={w / 2}
                        y={h / 2 + 4}
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="700"
                        fill={
                          isActive ? "#ffffff" : isDark ? "#cbd5e1" : "#475569"
                        }
                      >
                        {label}
                      </text>
                    </g>
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Section — stat grid + Patients Overview chart
// ---------------------------------------------------------------------------

function TherapistOverviewSection({ therapistData, loading, error,pulse,pulseKey}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const d = therapistData?.data;

  // Maps straight onto the sample payload the team shared:
  // { data: { TotalCounselling, "overall Bookings", "Apponietment today", patientsgraph: {...} } }
  // NewAppointments / CanceledAppointments / TotalVisitors and the `growth`
  // block are optional — once the real endpoint returns them, these cards
  // and their "Last year ±x%" pills will light up automatically.
  const stats = [
    {
      key: "bookings",
      icon: CalendarCheck,
      tint: "#34d9b8",
      title: "Overall Booking",
      value: d?.["overall Bookings"],
      delta: d?.growth?.overallBookings,
    },
    {
      key: "counselling",
      icon: Users,
      tint: "#8b7cf6",
      title: "Total Completed Sessions",
      value: d?.TotalCounselling,
      delta: d?.growth?.totalCounselling,
    },
    
    {
      key: "new",
      icon: UserPlus,
      tint: "#f6ad3c",
      title: "Upcoming Appointments",
      value: d?.upcomingAppointments,
      delta: d?.growth?.upcomingAppointments,
    },

    {
      key: "today",
      icon: CalendarClock,
      tint: "#5b8def",
      title: "Today Appointments",
      value: d?.["Apponietment today"],
      delta: d?.growth?.appointmentsToday,
    },
  ];

  return (
    <div className="mb-10">
       <PulseStyles />
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div
            className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em]"
            style={{ color: BRAND.pink }}
          >
            <Activity size={14} />
            Overview
          </div>
          <h1
            className={
              "text-4xl font-extrabold tracking-tight " + t.textPrimary
            }
          >
            Therapist Overview
          </h1>
          <p className={"mt-2 text-sm " + t.textMuted}>
            Live counselling activity across bookings, appointments and patient
            reach.
          </p>
        </div>
      </div>

      {error ? (
        <CardShell accent="pink">Unable to load therapist data.</CardShell>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-7">
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-1 lg:col-span-2">
            {stats.map((s) => (
              <TherapistStatCard
                key={s.key}
                icon={s.icon}
                tint={s.tint}
                title={s.title}
                value={loading ? undefined : (s.value ?? "--")}
                loading={loading}
                delta={s.delta}
                pulse={s.key === "counselling" && pulse}   // NEW
                pulseKey={pulseKey}            
              />
            ))}
          </div>

          <div className="lg:col-span-5">
            <PatientsOverviewChart
              therapistData={therapistData}
              loading={loading}
              error={error}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page-level component — self-fetching, drop-in anywhere.
//
// Props (all optional): pass these if a parent page already has the data
// (e.g. the combined dashboard) to skip this component's own fetch and
// avoid duplicate network calls.
// ---------------------------------------------------------------------------

export default function TherapistAnalytics({
  therapistData: therapistDataProp,
  loading: loadingProp,
  error: errorProp,
  wrapInPageShell = true,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const { code } = useAuth();

  const demo = DEMO_DATA[code]?.therapist;

  const isControlled = therapistDataProp !== undefined;

  const [therapistData, setTherapistData] = useState({});
  const [loading, setLoading] = useState(!demo);
  const [error, setError] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);

  const triggerPulse = useCallback(() => {
    setRefreshKey((k) => k + 1);
    setPulseKey((k) => k + 1);
    setPulse(true);
    setTimeout(() => setPulse(false), 1600);
  }, []);

  // NEW — live event, mirrors ta-test-notification handling in TestAnalyticsSection
  useEffect(() => {
    const handler = () => triggerPulse();
    window.addEventListener("ta-therapist-notification", handler);
    return () => window.removeEventListener("ta-therapist-notification", handler);
  }, [triggerPulse]);

  // NEW — replay a pending notification stored before navigation
  useEffect(() => {
    const raw = sessionStorage.getItem("pendingTherapistNotification");
    if (!raw) return;
    sessionStorage.removeItem("pendingTherapistNotification");
    triggerPulse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isControlled || demo) return; // parent is supplying data, skip own fetch

    const loadTherapistOverview = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(
          `${serverUrl}api/v1/users/therapist/${code}`,
        );

        if (!res.ok) {
          throw new Error("Failed to fetch therapist overview");
        }

        const json = await res.json();
        setTherapistData(json.data);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadTherapistOverview();
  }, [code, isControlled,refreshKey,demo]);

  const section = (
    <TherapistOverviewSection
      therapistData={isControlled ? therapistDataProp : demo ?? therapistData}
      loading={isControlled ? loadingProp : loading}
      error={isControlled ? errorProp : error}
      pulse={pulse}         // NEW
      pulseKey={pulseKey}  
    />
  );

  if (!wrapInPageShell) return section;

  return (
    <div className={"min-h-screen w-full " + t.pageWash}>
      <div className={"w-full px-4 py-8 sm:px-8 lg:px-12 " + t.textPrimary}>
        <div className="mx-auto max-w-6xl">{section}</div>
      </div>
    </div>
  );
}

// Exported for reuse/testing if a consuming page wants the raw section
// without the fetch wrapper or page shell.
export { TherapistOverviewSection };